/**
 * Hub settings (GET / PUT v3/hub/admin/settings). One page lists a section's keys; each key
 * is edited on its own page (pages, not pop-ups). Every save sends x-pcb-change-reason; a
 * 'typed' key also needs the key typed exactly (body.confirm). hub.port.live is read-only here
 * (it changes only through POST v3/admin/hub/live). hub.admin.upns can never lose the account
 * you are signed in with: the page warns, and the Hub refuses with WOULD_LOCK_OUT.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { msalInstance } from '@/auth/msal';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable } from '@/components/DataTable';
import { SelectField, TextArea, TextField } from '@/components/FormField';
import { ReasonField, asciiReason, reasonProblem } from '@/components/ChangeReason';
import { ConsentWaitingError, HubApiError, hubApi, type HubSetting, type HubSettingPutResult } from './hubApi';
import { HUB_BASE } from './HubArea';

const LIST_KINDS = new Set(['upn-list', 'host-suffix-list']);
const ADMIN_UPNS_KEY = 'hub.admin.upns';

function errorText(e: unknown): string {
  if (e instanceof ConsentWaitingError) return e.message;
  if (e instanceof HubApiError) return e.code ? `${e.message} (${e.code})` : e.message;
  return e instanceof Error ? e.message : String(e);
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Stored lists are comma-separated; edit them one per line. */
function toEditable(s: HubSetting): string {
  const v = s.value ?? '';
  return LIST_KINDS.has(s.kind) ? v.split(/[,;\r\n]+/).map((x) => x.trim()).filter(Boolean).join('\n') : v;
}

function useHubSettings(): { settings: HubSetting[] | null; error: string | null; reload: () => void } {
  const [settings, setSettings] = useState<HubSetting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setSettings(null);
    setError(null);
    hubApi
      .settings()
      .then((r) => {
        if (!cancelled) setSettings(r.settings);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorText(e));
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);
  return { settings, error, reload: () => setNonce((n) => n + 1) };
}

export function HubSettingsListPage({ section }: { section: 'configuration' | 'access' }): JSX.Element {
  const { settings, error } = useHubSettings();
  const rows = useMemo(() => (settings ?? []).filter((s) => s.section === section), [settings, section]);
  return (
    <div>
      <h2>{section === 'access' ? 'Hub access settings' : 'Hub settings'}</h2>
      <p className="page-intro">
        The hub.* settings in pcb-innovation-ops. Open a setting to change it; every change needs a reason and is recorded in
        the Hub&rsquo;s admin audit with the before and after values.
        {section === 'configuration' && (
          <>
            {' '}
            Access settings (admins, support roster) are under <Link to={`${HUB_BASE}/access/settings`}>Access and roles</Link>.
          </>
        )}
      </p>
      {error && <Banner tone="err">{error}</Banner>}
      {!settings && !error && <Spinner />}
      {settings && (
        <DataTable
          rows={rows}
          rowKey={(r) => r.key}
          emptyState={<div className="empty">No settings in this section.</div>}
          columns={[
            {
              header: 'Setting',
              sortKey: (r) => r.key,
              cell: (r) => (
                <Link to={`${HUB_BASE}/${section}/settings/${encodeURIComponent(r.key)}`}>
                  <code>{r.key}</code>
                </Link>
              ),
            },
            { header: 'What it does', cell: (r) => r.description },
            {
              header: 'Value',
              cell: (r) =>
                !r.exists ? (
                  <Pill tone="muted">not set</Pill>
                ) : (
                  <code className="value-clip">
                    {r.value}
                    {r.valueTruncated ? '…' : ''}
                  </code>
                ),
            },
            {
              header: 'Change',
              cell: (r) =>
                !r.editable ? (
                  <Pill tone="muted">read-only</Pill>
                ) : r.confirm === 'typed' ? (
                  <Pill tone="warn">typed confirmation</Pill>
                ) : (
                  <Pill tone="info">reason</Pill>
                ),
            },
            { header: 'Last changed', cell: (r) => (r.updatedAt ? `${fmt(r.updatedAt)} by ${r.updatedBy ?? '—'}` : '—') },
          ]}
        />
      )}
    </div>
  );
}

export function HubSettingEditPage({ section }: { section: 'configuration' | 'access' }): JSX.Element {
  const params = useParams();
  const key = decodeURIComponent(params['key'] ?? '');
  const { settings, error, reload } = useHubSettings();
  const setting = settings?.find((s) => s.key === key) ?? null;

  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [result, setResult] = useState<HubSettingPutResult | null>(null);

  useEffect(() => {
    if (setting) setValue(toEditable(setting));
  }, [setting]);

  const signedInUpn = (msalInstance.getAllAccounts()[0]?.username ?? '').toLowerCase();

  const lockOutWarning = useMemo(() => {
    if (key !== ADMIN_UPNS_KEY) return null;
    const list = value
      .split(/[,;\r\n]+/)
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
    if (list.length === 0) return 'The admin list can never be empty: nobody could administer the Hub.';
    if (signedInUpn && !list.includes(signedInUpn))
      return `The new list leaves out the account you are signed in with (${signedInUpn}). The Hub will refuse it; an alias does not count.`;
    return null;
  }, [key, value, signedInUpn]);

  const save = useCallback(async () => {
    if (!setting) return;
    setTried(true);
    setSaveError(null);
    setResult(null);
    if (reasonProblem(reason, { asciiOnly: true })) return;
    if (setting.confirm === 'typed' && confirm !== setting.key) return;
    if (lockOutWarning) return;
    const sendValue = LIST_KINDS.has(setting.kind)
      ? value.split(/[\r\n]+/).map((x) => x.trim()).filter(Boolean).join(',')
      : value;
    setSaving(true);
    try {
      const r = await hubApi.putSetting(
        setting.key,
        setting.kind === 'bool' ? sendValue === 'true' : sendValue,
        asciiReason(reason),
        setting.confirm === 'typed' ? confirm : undefined,
      );
      setResult(r);
      setReason('');
      setConfirm('');
      setTried(false);
      reload();
    } catch (e) {
      setSaveError(errorText(e));
    } finally {
      setSaving(false);
    }
  }, [setting, reason, confirm, value, lockOutWarning, reload]);

  const back = <Link to={`${HUB_BASE}/${section}/settings`}>Back to {section === 'access' ? 'access settings' : 'Hub settings'}</Link>;

  if (error) return <div>{back}<Banner tone="err">{error}</Banner></div>;
  if (!settings) return <Spinner />;
  if (!setting) return <div>{back}<div className="empty">No Hub setting called <code>{key}</code>.</div></div>;

  const readOnly = !setting.editable || setting.valueTruncated;

  return (
    <div>
      <p>{back}</p>
      <h2>
        <code>{setting.key}</code>
      </h2>
      <p className="page-intro">{setting.description}</p>
      <Card title="Current value">
        <p>
          {setting.exists ? <code className="value-block">{setting.value}</code> : <Pill tone="muted">not set</Pill>}
        </p>
        <p className="form-field__hint">
          Kind {setting.kind}. Last changed {fmt(setting.updatedAt)}
          {setting.updatedBy ? ` by ${setting.updatedBy}` : ''}.
        </p>
      </Card>

      {result && (
        <Banner tone="info">
          {result.changed ? 'Saved.' : 'Saved: the value was already that (recorded as unchanged).'} Before:{' '}
          <code>{result.before ?? '(none)'}</code> After: <code>{result.after ?? '(none)'}</code>
        </Banner>
      )}

      {readOnly ? (
        <Banner tone="warn">
          {setting.valueTruncated
            ? 'This value is too long to show in full, so it cannot be edited here.'
            : setting.key === 'hub.port.live'
              ? 'Read-only here: hub.port.live changes only through POST v3/admin/hub/live (the cutover route).'
              : 'Read-only here: this key is not in the Hub settings catalog.'}
        </Banner>
      ) : (
        <Card title="Change it">
          {setting.kind === 'bool' ? (
            <SelectField
              label="New value"
              value={value === 'true' || value === 'false' ? value : ''}
              onChange={setValue}
              placeholder="Choose…"
              options={[
                { value: 'true', label: 'true' },
                { value: 'false', label: 'false' },
              ]}
            />
          ) : LIST_KINDS.has(setting.kind) || setting.kind === 'json-alias-map' ? (
            <TextArea
              label="New value"
              value={value}
              onChange={setValue}
              rows={8}
              hint={setting.kind === 'json-alias-map' ? 'A JSON object: {"admin-account-upn": "person-upn"}.' : 'One per line.'}
            />
          ) : (
            <TextField label="New value" value={value} onChange={setValue} monospace />
          )}
          {lockOutWarning && <Banner tone="err">{lockOutWarning}</Banner>}
          <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
          {setting.confirm === 'typed' && (
            <TextField
              label={`Type ${setting.key} to confirm`}
              required
              value={confirm}
              onChange={setConfirm}
              monospace
              error={tried && confirm !== setting.key ? 'Type the setting name exactly.' : undefined}
            />
          )}
          {saveError && <Banner tone="err">{saveError}</Banner>}
          <div className="card-actions-row">
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save change'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
